import { useQuery } from '@apollo/client';
import { Button } from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import { Input } from 'components/Input/Input';
import { Label } from 'components/Label/Label';
import { Modal } from 'components/Modal/Modal';
import { Portal } from 'components/Portal/Portal';
import { Select } from 'components/Select/Select';
import { Textarea } from 'components/Textarea/Textarea';
import dayjs from 'dayjs';
import { normalizeArray } from 'lib/utils';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { COMPANY_BY_ID } from 'services/companies/query';

type AddCompanyTransactionModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (data: any) => void;
    isLoading?: boolean;
    editTransaction?: any;
    company: any;
};

export const AddCompanyTransactionModal: React.FC<AddCompanyTransactionModalProps> = ({
    isOpen,
    onClose,
    onAdd,
    isLoading = false,
    editTransaction,
    company,
}) => {
    const { t } = useTranslation('forms');
    const { t: p } = useTranslation('documents');

    const { data: companyData } = useQuery(COMPANY_BY_ID, {
        variables: { id: company?.id },
        skip: !company?.id,
    });

    const TYPES = [
        { value: 'CONSUMPTION', label: `${p('CONSUMPTION_TRANSACTIONS.CONSUMPTION')} (-)` },
        { value: 'WINE', label: `${p('CONSUMPTION_TRANSACTIONS.WINE')} (-)` },
        { value: 'CATERING', label: `${p('CONSUMPTION_TRANSACTIONS.CATERING')} (-)` },
        { value: 'DEBIT', label: `${p('CONSUMPTION_TRANSACTIONS.DEBIT')} (-)` },
        { value: 'DEPOSIT', label: `${p('CONSUMPTION_TRANSACTIONS.DEPOSIT')} (+)` },
        { value: 'CREDIT', label: `${p('CONSUMPTION_TRANSACTIONS.CREDIT')} (+)` },
        { value: 'REMAINDER_BALANCE_CZR', label: `${p('CONSUMPTION_TRANSACTIONS.REMAINDER_BALANCE_CZR')} (-)` },
        { value: 'REMAINDER_BALANCE', label: `${p('CONSUMPTION_TRANSACTIONS.REMAINDER_BALANCE')} (-)` },
    ];

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        setValue,
    } = useForm();

    useEffect(() => {
        setValue('date', dayjs().format('DD.MM.YYYY'));
    }, [setValue]);

    useEffect(() => {
        if (editTransaction) {
            setValue('date', dayjs(editTransaction.date).format('DD.MM.YYYY'));
            setValue('invoiceNumber', editTransaction.invoiceNumber);
            setValue('type', editTransaction.type);
            setValue('amount', editTransaction.amount);
            setValue('note', editTransaction.note);
            setValue('affectedBalance', editTransaction.affectedBalance === 'REMAINDER');
            setValue('member', editTransaction.member?.id);
        }
    }, [editTransaction, setValue]);

    const onSubmit = (data: any) => {
        data.affectedBalance = data.affectedBalance ? 'REMAINDER' : 'REGULAR';
        onAdd(data);
    };

    const memberOptions = (() => {
        let members: any[] = [];
        if (company?.members?.edges) {
            members = normalizeArray(company.members);
        } else if (companyData?.company?.members?.edges) {
            members = normalizeArray(companyData.company.members);
        }
        return members.map((member: any) => ({
            value: member.id,
            label: `${member.lastName}, ${member.firstName}`,
        }));
    })();

    return (
        <Portal>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                maxWidth={512}
            >
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">
                        {editTransaction ? p('editConsumptionTitle') : p('addConsumptionTitle')}
                    </h3>
                    {companyData?.company && (
                        <div className="flex gap-6 p-3 bg-zinc-50 rounded text-sm">
                            <div>
                                <span className="text-zinc-500">{p('currentBalance')}:</span>{' '}
                                <span className="font-semibold">
                                    {companyData.company.balance?.currency} {Number(companyData.company.balance?.amount || 0).toFixed(2)}
                                </span>
                            </div>
                            {Number(companyData.company.remainderBalance || 0) !== 0 && (
                                <div>
                                    <span className="text-zinc-500">{p('remainderBalance')}:</span>{' '}
                                    <span className="font-semibold">
                                        {companyData.company.balance?.currency} {Number(companyData.company.remainderBalance || 0).toFixed(2)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                    <Label htmlFor="member">{t('memberLabel')}</Label>
                    <Controller
                        name="member"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Select
                                value={memberOptions.find((opt: any) => opt.value === value)}
                                onChange={(val: any) => onChange(val?.value)}
                                options={memberOptions}
                                placeholder={t('selectMemberPlaceholder')}
                            />
                        )}
                    />
                </div>

                <div>
                    <Label htmlFor="date">{t('dateLabel')} *</Label>
                    <Input
                        {...register('date', { required: t('dateRequired') })}
                        placeholder="DD.MM.YYYY"
                        error={!!errors.date}
                    />
                    {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date.message as string}</p>}
                </div>

                <div>
                    <Label htmlFor="invoiceNumber">{t('invoiceNumberLabel')}</Label>
                    <Input {...register('invoiceNumber')} placeholder={t('invoiceNumberPlaceholder')} />
                </div>

                <div>
                    <Label htmlFor="type">{t('typeLabel')} *</Label>
                    <Controller
                        name="type"
                        control={control}
                        rules={{ required: t('typeRequired') }}
                        render={({ field: { onChange, value } }) => (
                            <Select
                                value={TYPES.find((opt: any) => opt.value === value)}
                                onChange={(val: any) => onChange(val?.value)}
                                options={TYPES}
                                placeholder={t('selectTypePlaceholder')}
                                error={!!errors.type}
                            />
                        )}
                    />
                    {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type.message as string}</p>}
                </div>

                <div>
                    <Label htmlFor="amount">{t('amountLabel')} *</Label>
                    <Input
                        {...register('amount', {
                            required: t('amountRequired'),
                            pattern: {
                                value: /^\d+(\.\d{1,2})?$/,
                                message: t('amountInvalid'),
                            },
                        })}
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        error={!!errors.amount}
                    />
                    {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount.message as string}</p>}
                </div>

                <div>
                    <Label htmlFor="note">{t('noteLabel')}</Label>
                    <Textarea {...register('note')} placeholder={t('notePlaceholder')} rows={3} />
                </div>

                <div className="flex items-center gap-2">
                    <Checkbox {...register('affectedBalance')} />
                    <Label htmlFor="affectedBalance">{t('affectedBalanceLabel')}</Label>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                        {p('cancelModalButton')}
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                        {isLoading ? p('saveButton') : p('saveButton')}
                    </Button>
                </div>
            </form>
            </div>
        </Modal>
        </Portal>
    );
};
